import React, { useState, useEffect } from 'react';
import { X, AlertCircle, User, Mail, Lock, Shield } from 'lucide-react';

function UserModal({ isOpen, onClose, onSubmit, user, mode = 'create' }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'client',
    is_active: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && mode === 'edit') {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '', // Don't populate password for edit
        role: user.role || 'client',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
    } else {
      // Reset form for create mode
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'client',
        is_active: true
      });
    }
    setErrors({});
  }, [user, mode, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (mode === 'create') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Password must contain an uppercase letter';
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password = 'Password must contain a lowercase letter';
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = 'Password must contain a number';
      }
    } else if (mode === 'edit' && formData.password) {
      // Only validate password in edit mode if it's provided
      if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Password must contain an uppercase letter';
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password = 'Password must contain a lowercase letter';
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = 'Password must contain a number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // For edit mode, only send fields that have values
      const dataToSubmit = { ...formData };
      if (mode === 'edit' && !dataToSubmit.password) {
        delete dataToSubmit.password; // Don't send empty password
      }

      await onSubmit(dataToSubmit);
      onClose();
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-virgo-navy-700 rounded-xl shadow-2xl max-w-md w-full mx-4 border border-virgo-navy-600">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-virgo-navy-600">
          <h2 className="text-xl font-semibold text-virgo-gold-400">
            {mode === 'create' ? 'Create New User' : 'Edit User'}
          </h2>
          <button
            onClick={onClose}
            className="text-virgo-teal-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errors.submit && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{errors.submit}</span>
            </div>
          )}

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Username
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className={`w-full pl-10 pr-4 py-2 bg-virgo-navy-800 border rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 text-white placeholder-virgo-navy-300 ${
                  errors.username ? 'border-red-500' : 'border-virgo-navy-600'
                }`}
                placeholder="john_doe"
              />
            </div>
            {errors.username && (
              <p className="mt-1 text-sm text-red-400">{errors.username}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full pl-10 pr-4 py-2 bg-virgo-navy-800 border rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 text-white placeholder-virgo-navy-300 ${
                  errors.email ? 'border-red-500' : 'border-virgo-navy-600'
                }`}
                placeholder="john@example.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Password {mode === 'edit' && <span className="text-virgo-navy-300">(leave blank to keep current)</span>}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className={`w-full pl-10 pr-4 py-2 bg-virgo-navy-800 border rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 text-white placeholder-virgo-navy-300 ${
                  errors.password ? 'border-red-500' : 'border-virgo-navy-600'
                }`}
                placeholder={mode === 'create' ? 'Minimum 8 characters' : 'Leave blank to keep current'}
              />
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-400">{errors.password}</p>
            )}
            {mode === 'create' && (
              <p className="mt-1 text-xs text-virgo-navy-300">
                Must include uppercase, lowercase, and a number
              </p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Role
            </label>
            <div className="relative">
              <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full pl-10 pr-4 py-2 bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 text-white appearance-none"
              >
                <option value="client">Client</option>
                <option value="staff">Staff</option>
                <option value="attorney">Attorney</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center bg-virgo-navy-800 p-3 rounded-lg border border-virgo-navy-600">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="h-4 w-4 text-virgo-teal-600 focus:ring-virgo-teal-500 border-virgo-navy-500 rounded bg-virgo-navy-700"
            />
            <label htmlFor="is_active" className="ml-3 block text-sm text-virgo-teal-300">
              Account is active
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-virgo-navy-600 border border-virgo-navy-500 rounded-lg text-virgo-teal-300 hover:bg-virgo-navy-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create User' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserModal;
